import * as React from 'react'
import { GripVerticalIcon } from 'lucide-react'
import {
  Group,
  Panel,
  Separator,
  type GroupImperativeHandle,
  type GroupProps,
  type LayoutStorage,
  type PanelProps,
  type SeparatorProps,
  useDefaultLayout,
} from 'react-resizable-panels'
import { cn } from '../cn'

/**
 * Backward-compatible wrapper around `react-resizable-panels`.
 *
 * The library renamed its public API in v4:
 *
 *   v2/v3                | v4
 *   ---------------------|--------------
 *   PanelGroup           | Group
 *   PanelResizeHandle    | Separator
 *   direction            | orientation
 *   onLayout             | onLayoutChange
 *   ImperativePanelGroupHandle | GroupImperativeHandle
 *   ref / forwardRef     | groupRef / panelRef props
 *
 * This wrapper preserves the v2 surface area (`<ResizablePanelGroup
 * direction='horizontal' onLayout={...}>`, `<ResizablePanel
 * defaultSize={...}>`, `<ResizableHandle />`) so call sites don't
 * have to change. v4 imperative refs (collapse / expand / resize)
 * are still available — pass them through as `groupRef` /
 * `panelRef` props.
 */

type ResizablePanelGroupProps = Omit<
  GroupProps,
  'orientation' | 'onLayoutChange'
> & {
  /** v2/v3 layout persistence id. Mapped to v4's `useDefaultLayout`. */
  autoSaveId?: string
  /** Required when the group conditionally renders panels at mount time. */
  panelIds?: string[]
  /** Optional storage implementation for persisted layouts. Defaults to localStorage. */
  storage?: LayoutStorage
  /** v4 `useDefaultLayout` debounce option. */
  debounceSaveMs?: number
  /** v2 alias for `orientation`. Defaults to "horizontal". */
  direction?: 'horizontal' | 'vertical'
  /** v2 alias for `onLayoutChange`. */
  onLayout?: GroupProps['onLayoutChange']
  /** v4 imperative handle ref. Pass via `groupRef` (not `ref`). */
  groupRef?: React.RefObject<GroupImperativeHandle | null>
}

export function ResizablePanelGroup({
  autoSaveId,
  panelIds,
  storage,
  debounceSaveMs,
  ...props
}: ResizablePanelGroupProps) {
  if (autoSaveId) {
    return (
      <PersistedResizablePanelGroup
        autoSaveId={autoSaveId}
        panelIds={panelIds}
        storage={storage}
        debounceSaveMs={debounceSaveMs}
        {...props}
      />
    )
  }

  return <ResizablePanelGroupBase {...props} />
}

function PersistedResizablePanelGroup({
  autoSaveId,
  panelIds,
  storage,
  debounceSaveMs,
  onLayout,
  onLayoutChanged,
  defaultLayout,
  ...props
}: ResizablePanelGroupProps & { autoSaveId: string }) {
  const safeStorage = useSafeLayoutStorage(storage)
  const savedLayout = useDefaultLayout({
    id: autoSaveId,
    panelIds,
    storage: safeStorage,
    debounceSaveMs,
  })

  const handleLayoutChange = React.useCallback<
    NonNullable<GroupProps['onLayoutChange']>
  >(
    (layout) => {
      savedLayout.onLayoutChange(layout)
      onLayout?.(layout)
    },
    [onLayout, savedLayout]
  )
  const handleLayoutChanged = React.useCallback<
    NonNullable<GroupProps['onLayoutChanged']>
  >(
    (layout) => {
      savedLayout.onLayoutChanged(layout)
      onLayoutChanged?.(layout)
    },
    [onLayoutChanged, savedLayout]
  )

  return (
    <ResizablePanelGroupBase
      {...props}
      defaultLayout={defaultLayout ?? savedLayout.defaultLayout}
      onLayout={handleLayoutChange}
      onLayoutChanged={handleLayoutChanged}
    />
  )
}

function useSafeLayoutStorage(storage?: LayoutStorage) {
  return React.useMemo<LayoutStorage>(() => {
    const getBaseStorage = () => {
      if (storage) return storage
      if (typeof window === 'undefined') return null

      return window.localStorage
    }

    return {
      getItem(key) {
        const baseStorage = getBaseStorage()
        if (!baseStorage) return null

        try {
          const value = baseStorage.getItem(key)
          if (value === null) return null

          JSON.parse(value)
          return value
        } catch {
          try {
            const removableStorage = baseStorage as LayoutStorage & {
              removeItem?: (key: string) => void
            }
            removableStorage.removeItem?.(key)
          } catch {
            return null
          }

          return null
        }
      },
      setItem(key, value) {
        const baseStorage = getBaseStorage()
        if (!baseStorage) return

        try {
          JSON.parse(value)
          baseStorage.setItem(key, value)
        } catch {
          return
        }
      },
    }
  }, [storage])
}

function ResizablePanelGroupBase({
  className,
  children,
  direction = 'horizontal',
  onLayout,
  groupRef,
  ...rest
}: ResizablePanelGroupProps) {
  return (
    <Group
      groupRef={groupRef}
      orientation={direction}
      onLayoutChange={onLayout}
      className={cn(
        'flex h-full w-full',
        direction === 'vertical' && 'flex-col',
        className
      )}
      {...rest}
    >
      {children}
    </Group>
  )
}

type ResizablePanelProps = PanelProps & { className?: string }

export function ResizablePanel({
  className,
  children,
  ...rest
}: ResizablePanelProps) {
  return (
    <Panel className={cn('relative h-full w-full', className)} {...rest}>
      {children}
    </Panel>
  )
}

type ResizableHandleProps = SeparatorProps & {
  className?: string
  /**
   * Use the corresponding parent group's orientation. v4's Separator
   * exposes `aria-orientation` on the rendered DOM, so the wrapper
   * applies orientation-conditional sizing via aria-attribute Tailwind
   * selectors — no explicit prop needed.
   */
  withHandle?: boolean
}

export function ResizableHandle({
  className,
  withHandle = false,
  ...rest
}: ResizableHandleProps) {
  return (
    <Separator
      className={cn(
        'bg-border relative flex w-px items-center justify-center',
        // A horizontal separator divides vertically stacked panels.
        'aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full',
        className
      )}
      {...rest}
    >
      {withHandle && (
        <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-sm border'>
          <GripVerticalIcon className='h-2.5 w-2.5' />
        </div>
      )}
    </Separator>
  )
}
